/* ============================================================================
   interact.js — the in-world interactivity layer for AGENTSEE

   The floor already answers two questions: "who is that?" (hover a person) and
   "what happens in there?" (click a room). This layer answers the other four a
   visitor actually asks once they are standing on it:

     what is this thing?          examine-on-hover: fresnel rim + a label
     what is that seat doing?     a desk lamp that warms, a page that riffles
     where am I / where are they? a ground ring, and an edge cue when they leave frame
     can I get there from here?   a footprint trail that stops at the wall

   THE CONSTRAINT that shapes every decision below: main.js caches materials in
   `MATS` and hands the SAME MeshStandardMaterial to dozens of meshes. Touching
   `mesh.material.emissive` to outline one object lights up the whole building.
   So nothing here ever writes to a host material — it draws its own geometry,
   or it clones a material once and swaps the pointer (occlusion fade only).

   THE SECOND CONSTRAINT: the host runs a 6-pass composer (post.js) into a
   HalfFloat target with UnrealBloom at threshold 0.86 *before* OutputPass.
   That is free long-range signalling: an emissive value above 1.0 halates and
   is legible from the plan view without a single extra pass, light, or pixel
   of overdraw. The seat lamps are built to exploit exactly that.

   COST — 8 draw calls at rest, 12 while hovering, 14 in walk mode:
     seat lamps 1 (InstancedMesh) · light pools 1 · pages 1
     focus ring 1 · inspect ring 1 · label sprite 1
     footprint trail 1 · landing ring 1                       ← walk mode only
     hover rim up to 4 (pooled, one hovered object at a time)
   No second render pass. No render target. No per-frame allocation: every
   Vector3 / Matrix4 / Quaternion / Color / Box3 below is module-scope scratch.

   ---------------------------------------------------------------------------
   ctx — everything comes from the host, nothing is imported from main.js:

     THREE          the host's three namespace (never import your own copy)
     scene          THREE.Scene
     world          the host's world Group — this layer parents itself here
     camera         THREE.PerspectiveCamera
     renderer       THREE.WebGLRenderer  (used only for domElement.style.cursor)
     seats          string[] of seat ids, or an object keyed by seat id
     getSeatAnchor(id)
                    -> THREE.Vector3 | {x,y,z,yaw} | null
                    the DESK SURFACE point the indicator sits on — not the
                    character. See the report for the exact host one-liner.

   optional, and each one degrades cleanly if absent:
     canStand(x,z)  -> boolean. Needed by the path preview and by the inspect
                      pose's wall-avoidance. Host: derive from collideWalk().
     controls       OrbitControls — read for controls.target when capturing the
                      pose to return to. Reconstructed from the camera if absent.
     onExamine(info, pose, object3d)   host tweens the camera to pose
     onRelease(pose)                   host tweens back
     seatName(id)   -> string for the off-screen cue. Falls back to the id.
     reducedMotion  boolean override; otherwise prefers-reduced-motion is read.
     domRoot        element for the off-screen cue. Defaults to document.body.
   ========================================================================== */

/* ---------- module-scope scratch: allocated once, reused forever ---------- */
let _T = null;                                   // set on first buildInteract
const _s = {                                     // filled in _initScratch(THREE)
  v1:null, v2:null, v3:null, v4:null, v5:null,
  m1:null, m2:null, q1:null, c1:null, c2:null,
  box:null, sph:null, ray:null, plane:null, ndc:null,
};
function _initScratch(T){
  if(_s.v1) return;
  _s.v1 = new T.Vector3(); _s.v2 = new T.Vector3(); _s.v3 = new T.Vector3();
  _s.v4 = new T.Vector3(); _s.v5 = new T.Vector3();
  _s.m1 = new T.Matrix4(); _s.m2 = new T.Matrix4(); _s.q1 = new T.Quaternion();
  _s.c1 = new T.Color();   _s.c2 = new T.Color();
  _s.box = new T.Box3();   _s.sph = new T.Sphere();
  _s.ray = new T.Ray();    _s.plane = new T.Plane(new T.Vector3(0,1,0), 0);
  _s.ndc = new T.Vector2();
}

/* ---------- the state language, in office terms ----------------------------
   Every seat reads at two ranges. FAR: the lamp head is an emissive cone; the
   host's bloom turns anything over ~0.86 into a warm halo you can pick out from
   the plan view. NEAR: the pool of light on the desk, and whether the page is
   flat or being worked.  The vocabulary is deliberately physical — no icons.

     idle      lamp on low, page flat, pool small        someone is at the desk
     thinking  lamp warms and breathes, page riffles     they are working
     speaking  lamp bright enough to halate, page still  they are talking
     walking   lamp drops to standby, pool collapses     the desk is empty
     blocked   lamp goes cold and stops moving           the desk has gone dark
   -------------------------------------------------------------------------- */
const SEAT_LOOK = {
  idle:     { lamp:0.34, pool:0.30, warm:[1.00,0.72,0.42], breathe:0.06, page:0.00 },
  thinking: { lamp:0.92, pool:0.72, warm:[1.00,0.78,0.46], breathe:0.34, page:1.00 },
  speaking: { lamp:2.10, pool:1.15, warm:[1.00,0.83,0.55], breathe:0.10, page:0.12 },
  walking:  { lamp:0.15, pool:0.11, warm:[1.00,0.70,0.42], breathe:0.02, page:0.00 },
  blocked:  { lamp:0.20, pool:0.16, warm:[0.55,0.63,0.80], breathe:0.00, page:0.00 },
};
const GOLD  = [0.85, 0.63, 0.35];                // the corridor-flow gold — the house accent for wayfinding
const SLATE = [0.42, 0.46, 0.55];                // "you can't get there"
const OFF   = [0, 0, 0];                         // a seat with no anchor yet: drawn nowhere, lit not at all

/* ---------- fresnel rim: the outline technique -----------------------------
   Draw the object's OWN geometry a second time with an additive fresnel
   material, coincident (three's default depthFunc is LessEqual, so a coincident
   draw passes without z-fighting) and swollen 4mm along the normal so it bleeds
   a hair past the silhouette. One extra draw call per mesh, at most four meshes,
   and only ever one object hovered — versus a backface shell, which needs a
   per-object geometry clone and breaks on off-centre or non-uniformly scaled
   props; or an emissive tweak, which is impossible here because the host shares
   materials. Crucially it is NOT a second render pass: no outline target, no
   depth prepass, no normal buffer, nothing added to the composer.
   -------------------------------------------------------------------------- */
const RIM_VERT = `
  uniform float uSwell;
  varying vec3 vN; varying vec3 vV;
  void main(){
    vec4 wp = modelMatrix * vec4(position + normal * uSwell, 1.0);
    vN = normalize(mat3(modelMatrix) * normal);
    vV = normalize(cameraPosition - wp.xyz);
    gl_Position = projectionMatrix * viewMatrix * wp;
  }`;
const RIM_FRAG = `
  uniform vec3  uColor;
  uniform float uOpacity;
  uniform float uPower;
  varying vec3 vN; varying vec3 vV;
  void main(){
    float f = 1.0 - abs(dot(normalize(vN), normalize(vV)));
    f = pow(clamp(f, 0.0, 1.0), uPower);
    gl_FragColor = vec4(uColor * f, f * uOpacity);
  }`;

/* ---------- little canvas factories (run once at build, never per frame) --- */
function radialTex(T, size = 128, hardness = 0.35){
  const c = document.createElement('canvas'); c.width = c.height = size;
  const g = c.getContext('2d');
  const r = size / 2;
  const grd = g.createRadialGradient(r, r, r * hardness, r, r, r);
  grd.addColorStop(0, 'rgba(255,255,255,1)');
  grd.addColorStop(0.55, 'rgba(255,255,255,0.42)');
  grd.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grd; g.fillRect(0, 0, size, size);
  const t = new T.CanvasTexture(c); t.colorSpace = T.SRGBColorSpace; return t;
}
function footTex(T){                              // a soft sole print, not a game arrow
  const c = document.createElement('canvas'); c.width = 48; c.height = 72;
  const g = c.getContext('2d');
  g.fillStyle = '#fff'; g.filter = 'blur(3px)';
  g.beginPath(); g.ellipse(24, 28, 13, 22, 0, 0, 7); g.fill();      // ball of the foot
  g.beginPath(); g.ellipse(24, 60, 9, 10, 0, 0, 7); g.fill();       // heel
  g.filter = 'none';
  const t = new T.CanvasTexture(c); t.colorSpace = T.SRGBColorSpace; return t;
}
function labelTex(T, title, sub){
  const px = 34, pad = 26, subPx = 20;
  const c = document.createElement('canvas'); const m = c.getContext('2d');
  const fT = `600 ${px}px Futura, Avenir, -apple-system, 'Helvetica Neue', Arial, sans-serif`;
  const fS = `500 ${subPx}px Futura, Avenir, -apple-system, 'Helvetica Neue', Arial, sans-serif`;
  m.font = fT; try{ m.letterSpacing = '5px'; }catch(e){}
  let w = m.measureText(title).width;
  if(sub){ m.font = fS; try{ m.letterSpacing = '4px'; }catch(e){} w = Math.max(w, m.measureText(sub).width); }
  c.width  = Math.ceil(w) + pad * 2;
  c.height = pad * 2 + px + (sub ? subPx + 10 : 0);
  const g = c.getContext('2d');
  g.textAlign = 'center'; g.textBaseline = 'middle';
  g.shadowColor = 'rgba(0,0,0,0.9)'; g.shadowBlur = 22;
  g.font = fT; try{ g.letterSpacing = '5px'; }catch(e){}
  g.fillStyle = '#ece6d7';
  g.fillText(title.toUpperCase(), c.width / 2, pad + px * 0.55);
  if(sub){
    g.font = fS; try{ g.letterSpacing = '4px'; }catch(e){}
    g.fillStyle = '#9c9689';
    g.fillText(sub.toUpperCase(), c.width / 2, pad + px + 4 + subPx * 0.5);
  }
  const t = new T.CanvasTexture(c); t.colorSpace = T.SRGBColorSpace; t.anisotropy = 8;
  return { tex: t, ar: c.width / c.height };
}

/* ========================================================================== */
export function buildInteract(ctx){
  const T = ctx.THREE;
  _T = T; _initScratch(T);

  const scene   = ctx.scene;
  const parent  = ctx.world || ctx.scene;
  const camera  = ctx.camera;
  const dom     = ctx.renderer && ctx.renderer.domElement;
  const domRoot = ctx.domRoot || document.body;
  const canStand = typeof ctx.canStand === 'function' ? ctx.canStand : null;
  const seatName = typeof ctx.seatName === 'function' ? ctx.seatName : (id => String(id).toUpperCase());

  const group = new T.Group();
  group.name = 'interact';
  group.matrixAutoUpdate = false;                 // it never moves; the host's world owns the transform
  parent.add(group);
  const UP = new T.Vector3(0, 1, 0);
  const ZERO = new T.Matrix4().makeScale(0, 0, 0);

  /* ---------- reduced motion: information survives, movement does not ------
     Every oscillation below is multiplied by MO. At MO=0 the lamps still say
     what the seat is doing (colour and brightness are the payload) — they just
     stop breathing, the ring stops turning, the page stops riffling, and every
     fade collapses to a near-instant crossfade. Nothing is hidden. */
  let MO = 1;
  const mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  const applyMotion = () => { MO = (ctx.reducedMotion !== undefined ? !ctx.reducedMotion : !(mq && mq.matches)) ? 1 : 0; };
  applyMotion();
  const onMQ = () => applyMotion();
  if(mq && mq.addEventListener) mq.addEventListener('change', onMQ);

  /* ================= shared geometry + materials (built once) ============= */
  const G = {
    lamp:  new T.ConeGeometry(0.062, 0.115, 12, 1, true),
    pool:  new T.PlaneGeometry(1, 1),
    page:  new T.PlaneGeometry(0.20, 0.27),
    ring:  new T.RingGeometry(0.60, 0.755, 56),
    ring2: new T.RingGeometry(0.26, 0.315, 40),
    foot:  new T.PlaneGeometry(0.16, 0.24),
  };
  G.pool.rotateX(-Math.PI / 2);                   // bake the lie-flat into the geometry, not the instance matrix
  G.ring.rotateX(-Math.PI / 2);
  G.ring2.rotateX(-Math.PI / 2);
  G.foot.rotateX(-Math.PI / 2);

  const TEX = { pool: radialTex(T, 128, 0.22), foot: footTex(T) };

  const MAT = {
    rim: new T.ShaderMaterial({
      uniforms: { uColor:{ value:new T.Color(0.95, 0.82, 0.58) }, uOpacity:{ value:0 }, uPower:{ value:2.4 }, uSwell:{ value:0.004 } },
      vertexShader: RIM_VERT, fragmentShader: RIM_FRAG,
      transparent:true, depthWrite:false, blending:T.AdditiveBlending, side:T.FrontSide,
    }),
    lamp: new T.MeshBasicMaterial({ color:0xffffff, side:T.DoubleSide, transparent:true, opacity:0.95, depthWrite:false, blending:T.AdditiveBlending }),
    pool: new T.MeshBasicMaterial({ color:0xffffff, map:TEX.pool, transparent:true, depthWrite:false, blending:T.AdditiveBlending }),
    page: new T.MeshStandardMaterial({ color:0xffffff, roughness:0.95, metalness:0, side:T.DoubleSide }),
    ring: new T.MeshBasicMaterial({ color:0xffffff, transparent:true, opacity:0, depthWrite:false, blending:T.AdditiveBlending, side:T.DoubleSide }),
    ring2:new T.MeshBasicMaterial({ color:0xffffff, transparent:true, opacity:0, depthWrite:false, blending:T.AdditiveBlending, side:T.DoubleSide }),
    foot: new T.MeshBasicMaterial({ color:0xffffff, map:TEX.foot, transparent:true, depthWrite:false, blending:T.AdditiveBlending }),
    label:new T.SpriteMaterial({ transparent:true, depthTest:false, opacity:0 }),
  };

  /* ================= 1 · examine-on-hover: rim pool + one label =========== */
  const RIM_MAX = 4;                              // one object is hovered at a time; four meshes is plenty for a GLTF prop
  const rims = [];
  for(let i = 0; i < RIM_MAX; i++){
    const m = new T.Mesh(G.lamp, MAT.rim);        // placeholder geometry, reassigned per hover
    m.matrixAutoUpdate = false; m.frustumCulled = false;
    m.visible = false; m.renderOrder = 8;
    group.add(m); rims.push(m);
  }
  let rimCount = 0, rimFade = 0, rimWant = 0;

  const label = new T.Sprite(MAT.label);
  label.renderOrder = 1002; label.visible = false; label.center.set(0.5, 0);
  group.add(label);
  const labelCache = new Map();                   // info.title -> {tex, ar} — a repeat hover redraws nothing
  let labelAr = 4, labelFade = 0, labelWant = 0, labelBase = _s.v1.clone();

  /* ================= 2 · in-world seat state ============================== */
  const seatIds = Array.isArray(ctx.seats) ? ctx.seats.slice()
                : (ctx.seats && typeof ctx.seats === 'object') ? Object.keys(ctx.seats) : [];
  const SEATN = seatIds.length;
  const seatIx = new Map();
  const seat = seatIds.map((id, i) => { seatIx.set(id, i); return {
    id, i,
    anchor: new T.Vector3(), yaw: 0, live: false,
    state: 'idle', look: SEAT_LOOK.idle, prev: SEAT_LOOK.idle,
    blend: 1,                                     // 0..1 crossfade between prev and look
    lamp: SEAT_LOOK.idle.lamp, pool: SEAT_LOOK.idle.pool, page: 0,
    phase: Math.random() * 6.28,
  }; });

  const iLamp = SEATN ? new T.InstancedMesh(G.lamp, MAT.lamp, SEATN) : null;
  const iPool = SEATN ? new T.InstancedMesh(G.pool, MAT.pool, SEATN) : null;
  const iPage = SEATN ? new T.InstancedMesh(G.page, MAT.page, SEATN) : null;
  [iLamp, iPool, iPage].forEach(m => { if(!m) return;
    m.frustumCulled = false; m.castShadow = false; m.receiveShadow = false;
    m.instanceMatrix.setUsage(T.DynamicDrawUsage);
    /* three seeds instanceMatrix with IDENTITY and instanceColor with WHITE, so
       any seat we never place would draw a lit unit-scale shape at the world
       origin. Collapse every instance first; refreshSeatAnchors() re-inflates
       only the seats the host could actually anchor. */
    _s.c1.setRGB(OFF[0], OFF[1], OFF[2]);
    for(let i = 0; i < SEATN; i++){ m.setMatrixAt(i, ZERO); m.setColorAt(i, _s.c1); }
    m.instanceMatrix.needsUpdate = true;
    if(m.instanceColor) m.instanceColor.needsUpdate = true;
    group.add(m);
  });
  if(iLamp){ iLamp.renderOrder = 5; iPool.renderOrder = 4; }
  let seatDirty = true;

  function refreshSeatAnchors(){                  // the host may not have built the desks when we were constructed
    if(typeof ctx.getSeatAnchor !== 'function') return;
    for(const s of seat){
      const a = ctx.getSeatAnchor(s.id);
      if(!a) continue;
      if(a.isVector3) s.anchor.copy(a);
      else s.anchor.set(a.x || 0, a.y !== undefined ? a.y : 0.78, a.z || 0);
      s.yaw = (a.yaw !== undefined ? a.yaw : 0);
      s.live = true;
    }
    seatDirty = true;
  }
  refreshSeatAnchors();

  /* ================= 3 · focus ring + off-screen cue ====================== */
  const focusRing = new T.Mesh(G.ring, MAT.ring);
  focusRing.visible = false; focusRing.renderOrder = 3; group.add(focusRing);
  let focusSeat = null, focusFade = 0, focusSpin = 0;

  const inspectRing = new T.Mesh(G.ring2, MAT.ring2);
  inspectRing.visible = false; inspectRing.renderOrder = 3; group.add(inspectRing);

  let followId = null;
  const cue = document.createElement('div');
  cue.style.cssText =
    'position:fixed;left:0;top:0;z-index:7;pointer-events:none;display:none;' +
    'font:500 10px/1 Futura,Avenir,system-ui,sans-serif;letter-spacing:.18em;text-transform:uppercase;' +
    'color:#e6dfcc;background:rgba(14,14,18,.66);border:1px solid rgba(216,160,90,.42);' +
    'padding:6px 10px 6px 22px;border-radius:2px;white-space:nowrap;' +
    'backdrop-filter:blur(6px);transform:translate(-50%,-50%);will-change:left,top';
  const cueArrow = document.createElement('span');
  cueArrow.style.cssText =
    'position:absolute;left:8px;top:50%;width:0;height:0;margin-top:-4px;' +
    'border-top:4px solid transparent;border-bottom:4px solid transparent;border-right:7px solid #d8a05a;' +
    'transform-origin:3.5px 4px';
  cue.appendChild(cueArrow);
  const cueText = document.createElement('span');
  cue.appendChild(cueText);
  domRoot.appendChild(cue);
  let cueOn = false, cueX = -1, cueY = -1, cueA = 0, cueId = undefined;

  /* ================= 4 · examinable props: registry ======================= */
  const reg = new Map();                          // root object3d -> entry
  const pickList = [];                            // roots, for the raycast
  const occluders = [];                           // entries with occlude:true
  const raycaster = new T.Raycaster();

  let hovered = null, forced = null;
  let inspecting = null, inspectReturn = null;
  let lastTapObj = null, lastTapT = 0;

  function collectMeshes(root, out){
    out.length = 0;
    root.traverse(o => {
      if(out.length >= RIM_MAX) return;
      if(o.isMesh && !o.isSkinnedMesh && o.visible && o.geometry) out.push(o);
    });
    return out;
  }
  const _meshBuf = [];

  function register(obj, info){
    if(!obj || reg.has(obj)) return obj;
    const e = {
      obj,
      title: (info && info.title) || obj.name || 'object',
      body:  (info && info.body)  || '',
      seat:  (info && info.seat)  || null,
      kind:  (info && info.kind)  || 'prop',
      face:  (info && info.face)  || null,          // optional [x,z] approach direction
      dynamic: !!(info && info.dynamic),
      occlude: !!(info && info.occlude),
      box: new T.Box3(),
      sph: new T.Sphere(),
      swaps: null,
      fade: 0, want: 0,
    };
    e.box.setFromObject(obj); e.box.getBoundingSphere(e.sph);
    reg.set(obj, e); pickList.push(obj);
    if(e.occlude){ prepOcclusion(e); occluders.push(e); }
    return obj;
  }
  function registerMany(list){
    if(!list) return;
    if(Array.isArray(list)){
      for(const it of list){
        if(!it) continue;
        if(Array.isArray(it)) register(it[0], it[1]);
        else if(it.object || it.obj) register(it.object || it.obj, it);
        else register(it, null);
      }
    } else for(const k in list) register(list[k], { title:k });
  }

  /* ================= 6 · occlusion fade ===================================
     DETECTION — no raycaster, no BVH, no per-frame scene walk. Each occluder
     gets its world AABB cached at register time. Every frame we build the
     segment camera -> the person you are talking to, and slab-test it against
     that short list (walls and big props that opted in: typically 10-30 boxes,
     each a handful of compares). Anything the segment enters before it reaches
     the target fades down. Boxes that contain the camera are skipped (you are
     up against it — fading it would open a hole in the building) and so are
     boxes that contain the target (never dissolve the desk they sit at).

     MATERIALS — the fade cannot write to the host's material, because the host
     shares one MeshStandardMaterial across dozens of walls. So each occluder
     gets its materials cloned ONCE at register time, already transparent with
     depthWrite off, and the fade swaps the pointer. Cloning eagerly matters:
     flipping transparent false->true later forces a shader recompile, and the
     resulting one-frame hitch would land exactly when the fade should be
     invisible. dispose() puts every original pointer back. */
  function prepOcclusion(e){
    e.swaps = [];
    e.obj.traverse(o => {
      if(!o.isMesh || !o.material) return;
      const orig = o.material;
      const arr = Array.isArray(orig) ? orig : [orig];
      const clones = arr.map(m => {
        const c = m.clone();
        c.transparent = true; c.depthWrite = false; c.opacity = 1;
        return c;
      });
      e.swaps.push({ mesh:o, orig, clone: Array.isArray(orig) ? clones : clones[0], list: clones, on:false });
    });
  }
  function applyOcclusion(e, k){                  // k = 0 clear .. 1 fully faded
    if(!e.swaps) return;
    const op = 1 - k * 0.86;                      // never to zero — a ghost of the wall keeps the space legible
    const on = k > 0.002;
    for(const s of e.swaps){
      if(on !== s.on){ s.mesh.material = on ? s.clone : s.orig; s.on = on; }
      if(on) for(const m of s.list) m.opacity = op;
    }
  }
  function clearOcclusion(){
    for(const e of occluders){ e.fade = 0; e.want = 0; applyOcclusion(e, 0); }
  }

  /* ================= 5 · path preview ===================================== */
  const FOOT_MAX = 26, FOOT_STEP = 0.42;
  const iFoot = new T.InstancedMesh(G.foot, MAT.foot, FOOT_MAX);
  iFoot.frustumCulled = false; iFoot.count = 0; iFoot.renderOrder = 4;
  iFoot.instanceMatrix.setUsage(T.DynamicDrawUsage);
  group.add(iFoot);
  const landRing = new T.Mesh(G.ring2, MAT.ring2.clone());
  landRing.visible = false; landRing.renderOrder = 4; group.add(landRing);
  landRing.material.opacity = 0;
  let pathFade = 0, pathWant = 0, pathBlocked = false;
  let ptrNDC = null, ptrIsTouch = false;

  /* ---------- the fixed cost of a preview, honestly stated ----------------
     We march the STRAIGHT line from where you stand to where you pointed and
     stop at the first sample canStand() rejects. The host's walkStep() does
     not walk a straight line — collideWalk() slides along the wall — so a
     blocked preview marks where the straight glide stops, not necessarily
     where you end up. That is the right promise for a preview: it never
     shows you walking through a wall, and it never over-claims a route. */
  function buildPath(dstX, dstZ){
    const ox = camera.position.x, oz = camera.position.z;
    let dx = dstX - ox, dz = dstZ - oz;
    const len = Math.hypot(dx, dz);
    if(len < 0.5){ iFoot.count = 0; pathWant = 0; return; }
    dx /= len; dz /= len;
    const px = -dz, pz = dx;                                   // perpendicular, for the left/right stagger
    const yaw = Math.atan2(dx, dz);
    const n = Math.min(FOOT_MAX, Math.max(2, Math.floor(len / FOOT_STEP)));
    let reach = n, blocked = false;
    if(canStand){
      for(let i = 1; i <= n; i++){
        const t = (i / n) * len;
        if(!canStand(ox + dx * t, oz + dz * t)){ reach = i - 1; blocked = true; break; }
      }
    }
    let k = 0;
    for(let i = 1; i <= reach && k < FOOT_MAX; i++, k++){
      const t = (i / n) * len;
      const side = (i & 1) ? 0.115 : -0.115;
      _s.v1.set(ox + dx * t + px * side, 0.028, oz + dz * t + pz * side);
      _s.q1.setFromAxisAngle(UP, yaw);
      _s.v2.set(1, 1, 1);
      _s.m1.compose(_s.v1, _s.q1, _s.v2);
      iFoot.setMatrixAt(k, _s.m1);
      const near = 1 - i / (n + 1);                            // the trail dissolves ahead of you
      const a = 0.30 + 0.55 * near;
      const c = blocked && i > reach - 2 ? SLATE : GOLD;
      _s.c1.setRGB(c[0] * a, c[1] * a, c[2] * a);
      iFoot.setColorAt(k, _s.c1);
    }
    iFoot.count = k;
    iFoot.instanceMatrix.needsUpdate = true;
    if(iFoot.instanceColor) iFoot.instanceColor.needsUpdate = true;
    const et = (reach / n) * len;
    landRing.position.set(ox + dx * et, 0.030, oz + dz * et);
    pathBlocked = blocked;
    pathWant = 1;
  }

  /* ================= hover plumbing ======================================= */
  function setHover(e){
    if(hovered === e) return;
    hovered = e;
    rimWant = e ? 1 : 0; labelWant = e ? 1 : 0;
    if(dom) dom.style.cursor = e ? 'pointer' : '';
    if(!e){ return; }
    /* rim: point the pooled meshes at this object's meshes — geometry pointers,
       not clones, so a hover allocates nothing. */
    collectMeshes(e.obj, _meshBuf);
    rimCount = Math.min(RIM_MAX, _meshBuf.length);
    for(let i = 0; i < RIM_MAX; i++){
      const r = rims[i];
      if(i < rimCount){ r.geometry = _meshBuf[i].geometry; r.visible = true; r.userData.src = _meshBuf[i]; }
      else { r.visible = false; r.userData.src = null; }
    }
    /* label */
    let L = labelCache.get(e.title);
    if(!L){ const mk = labelTex(T, e.title, e.kind && e.kind !== 'prop' ? e.kind : ''); L = { tex:mk.tex, ar:mk.ar }; labelCache.set(e.title, L); }
    MAT.label.map = L.tex; MAT.label.needsUpdate = true; labelAr = L.ar;
    e.box.setFromObject(e.obj); e.box.getBoundingSphere(e.sph);
    labelBase.set(e.sph.center.x, e.box.max.y + 0.10, e.sph.center.z);
    label.visible = true;
  }

  function hitTest(ndc){
    if(!ndc || !pickList.length) return null;
    _s.ndc.set(ndc.x, ndc.y);
    raycaster.setFromCamera(_s.ndc, camera);
    const hits = raycaster.intersectObjects(pickList, true);
    if(!hits.length) return null;
    let o = hits[0].object;
    while(o && !reg.has(o)) o = o.parent;
    return o ? reg.get(o) : null;
  }

  /* ================= 4 · the inspection pose maths ========================
     Frame the object the way a person picks something up: come from where the
     camera already is (a dolly-in, never a teleport around the object), stop at
     the distance that fits its bounding sphere in the NARROWER of the two
     frustum half-angles with a margin, and look slightly down at small things.
     The target is nudged off dead-centre so it reads as a shot, matching the
     house meetShot() idiom rather than a product photo.
     If canStand() is supplied, the pose is walked around the object in 18°
     steps until it lands somewhere the walker could stand; if no azimuth works
     it lifts into a high three-quarter that cannot be inside a wall. */
  function inspectPose(obj, opts){
    opts = opts || {};
    const e = reg.get(obj);
    if(e){ e.box.setFromObject(obj); e.box.getBoundingSphere(e.sph); _s.sph.copy(e.sph); }
    else { _s.box.setFromObject(obj); _s.box.getBoundingSphere(_s.sph); }
    const c = _s.sph.center, r = Math.max(0.06, _s.sph.radius);

    const fov = opts.fov || camera.fov;
    const vHalf = T.MathUtils.degToRad(fov) * 0.5;
    const hHalf = Math.atan(Math.tan(vHalf) * (opts.aspect || camera.aspect));
    const margin = opts.margin !== undefined ? opts.margin : 1.42;
    let dist = (r / Math.sin(Math.max(0.08, Math.min(vHalf, hHalf)))) * margin;
    /* the only hard floor is the near plane: sit closer than r + near and the
       object clips open. Anything more generous than that turns a close look at
       a phone into a mid-shot, which is the opposite of examining it. */
    dist = Math.max(dist, r + camera.near * 1.3, 0.42);
    dist = Math.min(dist, opts.maxDist || 9);

    /* approach azimuth: keep the camera's own side of the object */
    let az;
    if(e && e.face) az = Math.atan2(e.face[0], e.face[1]);
    else {
      _s.v1.set(camera.position.x - c.x, 0, camera.position.z - c.z);
      if(_s.v1.lengthSq() < 1e-6) _s.v1.set(0, 0, 1);
      az = Math.atan2(_s.v1.x, _s.v1.z);
    }
    /* elevation: look down at desk-scale things, level at furniture-scale */
    let elev = 0.36 - 0.19 * Math.min(1, r / 1.2);
    const side = (opts.side !== undefined) ? opts.side : ((obj.id % 2) ? 1 : -1);

    const place = (a, el) => {
      const ch = Math.cos(el), sh = Math.sin(el);
      _s.v2.set(Math.sin(a) * ch, sh, Math.cos(a) * ch).multiplyScalar(dist);
      return _s.v2;
    };
    let ok = false, tries = 0;
    let pos = new T.Vector3(), tgt = new T.Vector3();
    const AZ_STEPS = [0, 0.31, -0.31, 0.63, -0.63, 0.94, -0.94, 1.26, -1.26, 1.88, -1.88, 2.51, -2.51];
    for(; tries < AZ_STEPS.length; tries++){
      const a = az + AZ_STEPS[tries];
      pos.copy(c).add(place(a, elev));
      if(pos.y < 0.30) pos.y = 0.30;
      if(!canStand || canStand(pos.x, pos.z)){ az = a; ok = true; break; }
    }
    if(!ok){                                       // nowhere on the ring works: rise above it and look down
      elev = 0.78;
      pos.copy(c).add(place(az, elev));
      pos.y = Math.max(pos.y, c.y + r * 1.4 + 0.5);
    }
    /* off the third: shift the look-at across the frame and a touch up */
    _s.v3.set(Math.cos(az), 0, -Math.sin(az)).multiplyScalar(r * 0.24 * side);
    tgt.copy(c).add(_s.v3); tgt.y += r * 0.10;

    return { pos, target: tgt, dist, fov, azimuth: az, elevation: elev, radius: r };
  }

  function currentTarget(out){                     // what the camera is looking at right now
    if(ctx.controls && ctx.controls.target) return out.copy(ctx.controls.target);
    camera.getWorldDirection(_s.v4);
    return out.copy(camera.position).addScaledVector(_s.v4, 4);
  }

  function examine(obj){
    const e = reg.get(obj); if(!e) return false;
    const pose = inspectPose(obj, {});
    inspectReturn = { pos: camera.position.clone(), target: currentTarget(new T.Vector3()) };
    inspecting = e;
    inspectRing.position.set(e.sph.center.x, 0.032, e.sph.center.z);
    inspectRing.visible = true;
    setHover(null);
    if(typeof ctx.onExamine === 'function') ctx.onExamine({ title:e.title, body:e.body, kind:e.kind, seat:e.seat }, pose, obj);
    return true;
  }
  function releaseInspect(){
    if(!inspecting) return null;
    inspecting = null;
    const back = inspectReturn; inspectReturn = null;
    if(typeof ctx.onRelease === 'function') ctx.onRelease(back);
    return back;
  }

  /* ================= pointer API =========================================
     The host owns the events. This layer never attaches a listener to
     renderer.domElement — double-handling and undefined dispose ordering are
     not worth the convenience. onPointerDown is ADVISORY (it only records the
     press, because the host's click decision lives in pointerup after a 6px
     drag rejection); onPointerUp is the one that consumes. */
  function onPointerMove(ndc, ev){
    ptrNDC = ndc ? { x:ndc.x, y:ndc.y } : null;
    if(ev && ev.pointerType === 'touch'){ ptrIsTouch = true; return false; }
    ptrIsTouch = false;
    if(inspecting || forced) return false;
    const e = hitTest(ndc);
    setHover(e);
    return !!e;
  }
  function onPointerDown(ndc, ev){
    ptrNDC = ndc ? { x:ndc.x, y:ndc.y } : null;
    if(ev && ev.pointerType === 'touch'){
      ptrIsTouch = true;
      /* a press that is NOT on the pending object cancels beat two. This is the
         only place we can catch it: the host rejects a >6px drag before its
         pointerup branch runs, so a dragged tap never reaches onPointerUp and
         would otherwise leave the prop armed for the next tap anywhere. */
      if(lastTapObj){ const e = hitTest(ndc); if(!e || e.obj !== lastTapObj) lastTapObj = null; }
    }
    pathWant = 0;                                   // the trail gets out of the way of the press
    return false;                                   // never consumes: the host still needs its drag test
  }
  function onPointerUp(ndc, ev){
    if(inspecting){ releaseInspect(); return true; }   // a click anywhere puts it back
    const touch = ptrIsTouch || (ev && ev.pointerType === 'touch');
    const e = hitTest(ndc);
    if(!e){ lastTapObj = null; return false; }         // tapping away disarms the pending prop
    if(touch){
      /* hover has no meaning on touch, so the tap does the job in two beats:
         first tap rims the object and names it, second tap examines it. */
      const now = performance.now();
      if(lastTapObj === e.obj && now - lastTapT < 2600){ lastTapObj = null; return examine(e.obj); }
      lastTapObj = e.obj; lastTapT = now;
      setHover(e);
      return true;
    }
    return examine(e.obj);
  }

  /* ================= public state setters ================================= */
  function setSeatState(id, state){
    const i = seatIx.get(id); if(i === undefined) return;
    const s = seat[i];
    const look = SEAT_LOOK[state] || SEAT_LOOK.idle;   // unknown strings degrade to idle, never throw
    if(s.look === look){ s.state = state; return; }
    s.prev = s.look; s.look = look; s.state = state; s.blend = 0;
    seatDirty = true;
  }
  function setFocusSeat(id){
    focusSeat = (id !== undefined && id !== null && seatIx.has(id)) ? id : null;
  }
  function setFollowTarget(id){ followId = (id === undefined) ? null : id; }
  function highlight(obj){
    if(!obj){ forced = null; setHover(null); return; }
    const e = reg.get(obj); if(!e) return;
    forced = e; setHover(e);
  }

  /* ================= the frame ============================================ */
  const lerp = (a, b, k) => a + (b - a) * k;
  const damp = (dt, rate) => 1 - Math.exp(-rate * dt);   // frame-rate independent, and MO=0 makes it instant

  function update(dt, cam, pointerNDC, nav){
    const C = cam || camera;
    if(dt > 0.1) dt = 0.1;
    const t = (performance.now() * 0.001);
    const fast = MO ? damp(dt, 9) : 1;
    const slow = MO ? damp(dt, 5) : 1;
    if(pointerNDC) ptrNDC = pointerNDC;

    /* ---- 1 · rim + label ------------------------------------------------ */
    rimFade = lerp(rimFade, rimWant, fast);
    if(rimFade > 0.004){
      MAT.rim.uniforms.uOpacity.value = rimFade * (0.85 + (MO ? 0.15 * Math.sin(t * 2.4) : 0.15));
      group.updateMatrixWorld(true);                  // our own subtree only — ~10 nodes
      _s.m2.copy(group.matrixWorld).invert();
      for(let i = 0; i < rimCount; i++){
        const r = rims[i], src = r.userData.src;
        if(!src){ r.visible = false; continue; }
        src.updateWorldMatrix(true, false);
        r.matrix.multiplyMatrices(_s.m2, src.matrixWorld);
        r.matrixWorldNeedsUpdate = true;              // matrixAutoUpdate is off: say so explicitly
        r.visible = true;
      }
      for(let i = rimCount; i < RIM_MAX; i++) rims[i].visible = false;
    } else if(rims[0].visible){
      for(const r of rims){ r.visible = false; r.userData.src = null; }
      rimCount = 0;
    }

    labelFade = lerp(labelFade, labelWant, fast);
    if(labelFade > 0.004 && hovered){
      MAT.label.opacity = labelFade;
      const h = 0.26 + Math.min(0.14, hovered.sph.radius * 0.10);
      label.scale.set(h * labelAr, h, 1);
      label.position.copy(labelBase);
      label.position.y += (1 - labelFade) * -0.06;      // it rises the last few centimetres as it arrives
      label.visible = true;
    } else if(label.visible && labelFade <= 0.004){ label.visible = false; MAT.label.opacity = 0; }

    /* ---- 2 · seat indicators -------------------------------------------- */
    if(SEATN && iLamp){
      let wrote = false;
      for(const s of seat){
        if(!s.live) continue;
        if(s.blend < 1){ s.blend = Math.min(1, s.blend + (MO ? dt * 1.9 : 1)); seatDirty = true; }
        const b = s.blend * s.blend * (3 - 2 * s.blend);
        const L = s.look, P = s.prev;
        const breathe = MO ? (1 + lerp(P.breathe, L.breathe, b) * Math.sin(t * 1.55 + s.phase)) : 1;
        const lampI = lerp(P.lamp, L.lamp, b) * breathe;
        const poolI = lerp(P.pool, L.pool, b) * breathe;
        const pageK = lerp(P.page, L.page, b);
        const moved = Math.abs(lampI - s.lamp) > 0.0015 || Math.abs(poolI - s.pool) > 0.0015 || Math.abs(pageK - s.page) > 0.0015;
        if(!moved && !seatDirty) continue;
        s.lamp = lampI; s.pool = poolI; s.page = pageK;
        wrote = true;

        /* the tint is the crossfaded warm, held at unit magnitude in c1 so both
           the lamp and its pool can scale it independently without recomputing */
        _s.c1.setRGB(P.warm[0], P.warm[1], P.warm[2]);
        _s.c2.setRGB(L.warm[0], L.warm[1], L.warm[2]);
        _s.c1.lerp(_s.c2, b);

        const a = s.anchor, cy = Math.cos(s.yaw), sy = Math.sin(s.yaw);
        _s.q1.setFromAxisAngle(UP, s.yaw);

        /* lamp head: back-left of the desk from the sitter's point of view.
           lampI passes 1.0 on 'speaking', clears the host's 0.86 bloom cut and
           halates — that is the whole across-the-floor read, for free. */
        _s.v1.set(a.x + cy * -0.26 + sy * -0.20, a.y + 0.36, a.z + sy * -0.26 + cy * 0.20);
        _s.m1.compose(_s.v1, _s.q1, _s.v2.set(1, 1, 1));
        iLamp.setMatrixAt(s.i, _s.m1);
        _s.c2.copy(_s.c1).multiplyScalar(lampI);
        iLamp.setColorAt(s.i, _s.c2);

        /* the pool of light it throws, on the desk — the close-up read */
        const pw = 0.52 + poolI * 0.42;
        _s.v1.set(a.x + cy * -0.20, a.y + 0.006, a.z + sy * -0.20);
        _s.m1.compose(_s.v1, _s.q1, _s.v2.set(pw, 1, pw));
        iPool.setMatrixAt(s.i, _s.m1);
        _s.c2.copy(_s.c1).multiplyScalar(poolI * 0.72);
        iPool.setColorAt(s.i, _s.c2);

        /* the page: flat when idle, lifting and riffling while they think */
        const lift = pageK * (MO ? (0.5 + 0.5 * Math.sin(t * 3.1 + s.phase * 2)) : 0.5);
        _s.v1.set(a.x + cy * 0.13, a.y + 0.004 + lift * 0.035, a.z + sy * 0.13);
        _s.q1.setFromAxisAngle(UP, s.yaw);
        _s.m2.makeRotationX(-Math.PI / 2 + lift * 0.42);
        _s.m1.compose(_s.v1, _s.q1, _s.v2.set(1, 1, 1)).multiply(_s.m2);
        iPage.setMatrixAt(s.i, _s.m1);
        const pd = s.look === SEAT_LOOK.blocked ? 0.30 : 0.86;
        _s.c1.setRGB(pd, pd * 0.97, pd * 0.90);
        iPage.setColorAt(s.i, _s.c1);
      }
      if(wrote || seatDirty){
        iLamp.instanceMatrix.needsUpdate = iPool.instanceMatrix.needsUpdate = iPage.instanceMatrix.needsUpdate = true;
        if(iLamp.instanceColor) iLamp.instanceColor.needsUpdate = true;
        if(iPool.instanceColor) iPool.instanceColor.needsUpdate = true;
        if(iPage.instanceColor) iPage.instanceColor.needsUpdate = true;
        seatDirty = false;
      }
    }

    /* ---- 3 · focus ring + off-screen cue -------------------------------- */
    const fWant = focusSeat ? 1 : 0;
    focusFade = lerp(focusFade, fWant, slow);
    if(focusFade > 0.004){
      const i = seatIx.get(focusSeat);
      const s = i !== undefined ? seat[i] : null;
      if(s){
        focusSpin += MO ? dt * 0.30 : 0;
        const br = MO ? (1 + 0.035 * Math.sin(t * 1.7)) : 1;
        focusRing.position.set(s.anchor.x, 0.026, s.anchor.z);
        focusRing.rotation.y = focusSpin;
        focusRing.scale.setScalar(br * (0.72 + 0.28 * focusFade));
        MAT.ring.color.setRGB(GOLD[0], GOLD[1], GOLD[2]);
        MAT.ring.opacity = focusFade * 0.55;
        focusRing.visible = true;
      }
    } else if(focusRing.visible){ focusRing.visible = false; MAT.ring.opacity = 0; }

    if(inspecting){
      inspectRing.visible = true;
      MAT.ring2.color.setRGB(GOLD[0], GOLD[1], GOLD[2]);
      MAT.ring2.opacity = 0.42 + (MO ? 0.10 * Math.sin(t * 2.2) : 0.06);
      inspectRing.rotation.y += MO ? dt * 0.5 : 0;
    } else if(inspectRing.visible){ inspectRing.visible = false; MAT.ring2.opacity = 0; }

    updateCue(C);

    /* ---- 5 · path preview ----------------------------------------------- */
    if(nav === 'walk' && ptrNDC && !inspecting && !hovered){
      _s.ndc.set(ptrNDC.x, ptrNDC.y);
      raycaster.setFromCamera(_s.ndc, C);
      if(raycaster.ray.intersectPlane(_s.plane, _s.v5)) buildPath(_s.v5.x, _s.v5.z);
      else pathWant = 0;
    } else pathWant = 0;
    pathFade = lerp(pathFade, pathWant, fast);
    if(pathFade > 0.004 && iFoot.count){
      MAT.foot.opacity = pathFade;
      iFoot.visible = true;
      const c = pathBlocked ? SLATE : GOLD;
      landRing.material.color.setRGB(c[0], c[1], c[2]);
      landRing.material.opacity = pathFade * (pathBlocked ? 0.34 : 0.52);
      landRing.scale.setScalar(1 + (MO ? 0.06 * Math.sin(t * 3.4) : 0));
      landRing.visible = true;
    } else { iFoot.visible = false; landRing.visible = false; }

    /* ---- 6 · occlusion fade --------------------------------------------- */
    updateOcclusion(C, dt, nav, fast);
  }

  /* ---- the off-screen cue: pure DOM, so it costs nothing in the composer - */
  function updateCue(C){
    let anchor = null;
    if(followId !== null && followId !== undefined){
      const i = seatIx.get(followId);
      if(i !== undefined && seat[i].live) anchor = seat[i].anchor;
      else if(typeof ctx.getSeatAnchor === 'function'){
        const a = ctx.getSeatAnchor(followId);
        if(a) anchor = a.isVector3 ? a : _s.v3.set(a.x || 0, a.y || 1.2, a.z || 0);
      }
    }
    if(!anchor){ if(cueOn){ cue.style.display = 'none'; cueOn = false; } return; }
    _s.v4.set(anchor.x, anchor.y + 0.9, anchor.z).project(C);
    const behind = _s.v4.z > 1;
    const off = behind || _s.v4.x < -0.96 || _s.v4.x > 0.96 || _s.v4.y < -0.96 || _s.v4.y > 0.96;
    if(!off){ if(cueOn){ cue.style.display = 'none'; cueOn = false; } return; }
    let nx = _s.v4.x, ny = _s.v4.y;
    if(behind){ nx = -nx; ny = -ny; }
    const m = Math.max(Math.abs(nx), Math.abs(ny)) || 1;
    nx /= m; ny /= m;                                 // push it out to the frame edge
    const pad = 64;
    const x = Math.max(pad, Math.min(innerWidth  - pad, (nx * 0.5 + 0.5) * innerWidth));
    const y = Math.max(pad, Math.min(innerHeight - pad, (-ny * 0.5 + 0.5) * innerHeight));
    const a = Math.round(Math.atan2(-ny, nx) * 180 / Math.PI) + 180;
    /* the only string building in the whole frame path, and only while the cue
       is actually up — gated on real movement so a still target writes nothing */
    if(Math.abs(x - cueX) > 0.5 || Math.abs(y - cueY) > 0.5){
      cueX = x; cueY = y;
      cue.style.left = x + 'px'; cue.style.top = y + 'px';
    }
    if(a !== cueA){ cueA = a; cueArrow.style.transform = 'rotate(' + a + 'deg)'; }
    if(cueId !== followId){ cueId = followId; cueText.textContent = seatName(followId); }
    if(!cueOn){ cue.style.display = 'block'; cueOn = true; }
  }

  /* ---- the slab test, once per frame over a short list ------------------- */
  function updateOcclusion(C, dt, nav, fast){
    if(!occluders.length) return;
    /* only inside: from the plan view the roof is already off and fading walls
       would just punch holes in a model you are looking down into. */
    const inside = nav === 'walk' || C.position.y < 2.6;
    let tgt = null;
    if(inside){
      const id = focusSeat !== null && focusSeat !== undefined ? focusSeat : followId;
      const i = seatIx.get(id);
      if(i !== undefined && seat[i].live) tgt = seat[i].anchor;
    }
    if(!tgt){
      let any = false;
      for(const e of occluders) if(e.want !== 0 || e.fade > 0.002){ e.want = 0; any = true; }
      if(!any) return;
    } else {
      _s.v1.set(tgt.x, tgt.y + 0.75, tgt.z);
      _s.v2.copy(_s.v1).sub(C.position);
      const segLen = _s.v2.length();
      if(segLen < 0.4){ for(const e of occluders) e.want = 0; }
      else {
        _s.v2.divideScalar(segLen);
        _s.ray.set(C.position, _s.v2);
        for(const e of occluders){
          if(e.dynamic) e.box.setFromObject(e.obj);
          const b = e.box;
          const blocks =
            !b.containsPoint(C.position) &&                       // you are up against it — leave it solid
            !b.containsPoint(_s.v1) &&                            // never dissolve the desk they sit at
            !!_s.ray.intersectBox(b, _s.v3) &&
            _s.v3.distanceTo(C.position) < segLen - 0.25;
          e.want = blocks ? 1 : 0;
        }
      }
    }
    for(const e of occluders){
      let f = lerp(e.fade, e.want, fast);
      if(Math.abs(f - e.want) < 0.0015) f = e.want;   // snap, so a settled wall stops writing opacities
      if(f === e.fade) continue;
      e.fade = f; applyOcclusion(e, f);
    }
  }

  /* ================= teardown ============================================= */
  function dispose(){
    clearOcclusion();
    for(const e of occluders){                       // put every host material pointer back
      if(!e.swaps) continue;
      for(const s of e.swaps){ s.mesh.material = s.orig; for(const m of s.list) m.dispose(); }
      e.swaps = null;
    }
    occluders.length = 0; pickList.length = 0; reg.clear();
    if(mq && mq.removeEventListener) mq.removeEventListener('change', onMQ);
    if(cue.parentNode) cue.parentNode.removeChild(cue);
    if(dom) dom.style.cursor = '';
    /* the rims hold BORROWED geometry pointers — hand them back our own
       placeholder before anything is disposed, or we free the host's props */
    for(const r of rims){ r.visible = false; r.userData.src = null; r.geometry = G.lamp; }
    if(group.parent) group.parent.remove(group);
    group.traverse(o => { if(o.isInstancedMesh) o.dispose(); });
    for(const k in G) G[k].dispose();
    for(const k in MAT) MAT[k].dispose();
    landRing.material.dispose();
    for(const k in TEX) TEX[k].dispose();
    for(const L of labelCache.values()) L.tex.dispose();
    labelCache.clear();
    hovered = forced = inspecting = null; inspectReturn = null;
  }

  return {
    register, registerMany, update,
    onPointerMove, onPointerDown, onPointerUp,
    setSeatState, highlight, dispose, group,
    /* the rest of the surface the host needs */
    setFocusSeat, setFollowTarget,
    examine, releaseInspect, inspectPose,
    refreshSeatAnchors,
    get inspecting(){ return inspecting ? inspecting.obj : null; },
    get hovered(){ return hovered ? hovered.obj : null; },
  };
}
